import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import SyntaxHint from "./SyntaxHint";

import { updateSection } from "../../../assignmentSlice";
import { gradeInterpretationAPI } from "../../../assignmentAPI";
import { useTTS } from "../../../../../hooks/useTTS";
import {
  useSpeechRecognition,
  calculateReadingAccuracy,
  calculateWordsPerMinute,
} from "../../../../../hooks/useSpeechRecognition";
import { useAudioRecorder } from "../../../../../hooks/useAudioRecorder";
import { getContentHeight } from "../../../../../utils/textUtils";
import StepsBar from "../../widgets/StepsBar";
import "./DirectTranslationPage.css";

// 직독직해 — 끊어읽기 단위로 문장을 읽어 나가는 화면.
//
// 지인에듀가 주는 sentence_translation_data 는 영어와 우리말이 같은 자리에서
// " / " 로 끊겨 있다. 그 덩어리끼리 짝을 지어 위아래로 붙여 보여준다.
// 덩어리 수가 어긋나면 짝을 짓지 않고 문장 통째로 보여준다(원본이 늘 맞는다는
// 보장이 없고, 어긋난 채로 짝을 지으면 엉뚱한 해석이 붙는다).

const CIRCLED = "①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳";
const numberOf = (index) => (index < 20 ? CIRCLED[index] : `${index + 1}.`);

// 문장을 다 읽고 다음 문장으로 넘어가기 전에 쉬는 시간
const SENTENCE_GAP_MILLISECONDS = 700;
// 덩어리 하나를 읽고 다음 덩어리로 넘어가기 전에 쉬는 시간. 끊어읽기의 리듬이라
// 문장 사이보다 짧아야 한 문장이 토막 난 것처럼 들리지 않는다.
const CHUNK_GAP_MILLISECONDS = 350;
// 들려주기가 끝난 뒤 마이크를 열기까지 두는 틈. 소리의 꼬리가 마이크로 새면
// 기계 목소리를 아이 목소리로 받아쓴다.
const SPEECH_TAIL_MILLISECONDS = 400;
// 아이가 스스로 떠올려 말할 때 주는 시간. 그 토막을 읽는 시간의 몇 배로 준다.
// 우리말을 읽고, 영어를 떠올리고, 입을 떼기까지가 다 이 안에 들어가야 한다.
const STUDENT_THINKING_RATIO = 2.5;

// 마이크를 열어 두는 시간의 아래·위 한계. 배수로만 정하면 짧은 토막에서 입도 떼기
// 전에 끊기고, 긴 문장에서는 말이 끝난 뒤로도 한참 열려 있게 된다.
// **아이가 '다 읽었어요' 로 먼저 끝내는 길은 그대로다.** 이것은 스스로 끝내지 않는
// 단말을 위한 상한일 뿐이다.
const MINIMUM_LISTEN_MILLISECONDS = 10000;
const MAXIMUM_LISTEN_MILLISECONDS = 60000;

// 목소리가 없는 단말에서 speechSynthesis 가 끝났다는 신호를 주지 않을 때 쓰는
// 상한. 문장 길이에 맞춰 잡는다. 이 값으로 아이를 기다리지는 않는다. 아이는
// 스페이스바(모바일은 버튼)로 스스로 넘긴다.
const WORD_MILLISECONDS = 420;
const MINIMUM_SPEECH_MILLISECONDS = 800;

const estimateSpeechMilliseconds = (text) => {
  const wordCount = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(MINIMUM_SPEECH_MILLISECONDS, wordCount * WORD_MILLISECONDS);
};

// 지금 누구 차례인지 아이에게 보여주는 말. 이모지를 쓰지 않는다. 글꼴이 없는
// 단말에서 네모로 나오고, 색으로 이미 둘을 가르고 있다.
const TURN_LABEL = {
  listen: "들어보세요",
  repeat: "따라 읽어요",
  student_first: "영어로 말해 보세요",
};

/** 듣는 동안 물결치는 음파. 말소리가 잡히면(isActive) 크게 뛴다. */
const WaveformMark = ({ isActive }) => (
  <span
    className={`direct-translation__waveform${isActive ? " is-active" : ""}`}
  >
    {[0, 1, 2, 3, 4].map((barIndex) => (
      <span key={barIndex} className="direct-translation__waveform-bar" />
    ))}
  </span>
);

/** 녹음 중임을 알리는 마이크 그림. 글자만으로는 켜졌는지 알 수가 없다. */
const MicrophoneMark = () => (
  <svg
    className="direct-translation__mic"
    viewBox="0 0 24 24"
    role="img"
    aria-label="녹음 중"
  >
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path
      d="M5 11a7 7 0 0 0 14 0"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path d="M12 18v3" fill="none" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// 아이가 넘겨 주기를 기다리는 차례
const STUDENT_TURN_LIST = ["repeat", "student_first"];

// 이만큼 맞으면 통과로 본다. 받아쓰기가 관사나 복수형을 자주 흘리므로 100 을
// 요구하면 잘 읽은 아이도 계속 막힌다.
// **선생님이 출제할 때 고른 값이 있으면 그것을 쓴다.** 이것은 못 골랐을 때의 기본값이다.
const PASSING_ACCURACY = 70;
// 이 아래는 아예 못 읽은 것으로 보고 X 를 준다. 사이는 세모다.
const PARTIAL_ACCURACY = 40;

// 목소리를 못 들려줄 때 점수만 보여주고 서 있는 시간. 1.1초로 뒀더니 번쩍하고
// 사라져 아이가 점수를 못 봤다.
const RESULT_SHOW_MILLISECONDS = 2600;

// 아이에게 보여줄 결과. 점수만 있으면 잘한 건지 아닌지 한눈에 안 들어온다.
const gradeOf = (accuracy, passingAccuracy = PASSING_ACCURACY) => {
  // 추임새는 영어로 짧게 준다. "분당 몇 단어" 같은 수치는 아이에게 아무 뜻이 없다.
  // 점수가 올라갈수록 세게 간다.
  if (accuracy >= 95) {
    return { key: "pass", message: "Perfect!", detail: "완벽해요" };
  }
  if (accuracy >= 85) {
    return { key: "pass", message: "Excellent!", detail: "아주 잘했어요" };
  }
  if (accuracy >= passingAccuracy) {
    return { key: "pass", message: "Good job!", detail: "잘했어요" };
  }
  if (accuracy >= PARTIAL_ACCURACY) {
    return { key: "partial", message: "Almost!", detail: "거의 다 왔어요" };
  }
  return { key: "fail", message: "One more time!", detail: "한 번 더 읽어 볼까요" };
};

/**
 * 결과 기호를 글자가 아니라 그림으로 그린다.
 *
 * `○ △ ✕` 는 글꼴마다 굵기와 크기가 제각각이라 셋을 나란히 두면 어느 하나가
 * 유난히 작거나 삐뚤어 보인다. 같은 자에 같은 굵기로 그려야 한 벌로 보인다.
 */
const GradeMark = ({ gradeKey }) => (
  <svg
    className="direct-translation__score-mark"
    viewBox="0 0 100 100"
    role="img"
    aria-label={gradeKey}
  >
    <circle className="direct-translation__score-mark-disc" cx="50" cy="50" r="46" />
    {gradeKey === "pass" && (
      <circle
        className="direct-translation__score-mark-stroke"
        cx="50"
        cy="50"
        r="26"
      />
    )}
    {gradeKey === "partial" && (
      <path
        className="direct-translation__score-mark-stroke"
        d="M50 26 L74 68 H26 Z"
        strokeLinejoin="round"
      />
    )}
    {gradeKey === "fail" && (
      <path
        className="direct-translation__score-mark-stroke"
        d="M32 32 L68 68 M68 32 L32 68"
        strokeLinecap="round"
      />
    )}
  </svg>
);

// 교사가 출제 화면에서 고르는 단계. teacher 의 WorksheetModal/constants.jsx 의
// direct_translation options 와 key 가 같아야 한다.
//
// 세 단계가 하는 일이 다르다.
// - listening: 듣기만 한다. 마이크를 열지 않는다.
// - fluency:   들은 대로 영어를 소리 내어 읽는다. 영어 인식으로 채점한다.
// - interpretation: 영어만 보고 우리말로 통역해 말한다. 우리말 인식으로 받아쓰고
//   **AI 가 뜻을 본다.** 우리말은 어순과 표현이 자유로워 단어 대조로는 채점이 안 된다.
export const MODE_LIST = [
  {
    key: "listening",
    value: "듣기",
    hint: "영어를 끊어 들려줍니다. 우리말과 함께 눈으로 따라가세요.",
    needsMicrophone: false,
    language: null,
  },
  {
    key: "fluency",
    value: "유창성",
    hint: "들은 대로 소리 내어 읽어 보세요.",
    needsMicrophone: true,
    language: "en-US",
    // 마이크를 열어 두는 시간을 그 토막 읽는 시간의 몇 배로 줄지. 읽기는 들은 대로
    // 소리 내는 것이라 읽는 시간에 더듬는 여유만 얹으면 된다.
    listenRatio: 2.5,
  },
  {
    key: "interpretation",
    value: "통역",
    hint: "영어를 보고 우리말로 통역해 말해 보세요.",
    needsMicrophone: true,
    language: "ko-KR",
    // **통역은 넉넉히 준다.** 영어를 읽고, 뜻을 옮기고, 우리말 문장을 만들어 말하기
    // 까지가 다 이 안에 들어간다. 문장 단위로 하면 특히 길다.
    listenRatio: 4,
  },
];

// 듣기 모드에서 무엇을 위에 둘지. 우리말을 보고 영어를 떠올리고 싶을 때가 있어
// 뒤집기를 둔다. 숨기기는 영어만 보고 스스로 해석해 보는 것이다.
const LISTENING_LAYOUT_LIST = [
  { key: "english", value: "영어+해석" },
  { key: "korean", value: "해석+영어" },
  { key: "hidden", value: "해석 숨기기" },
];

// 글자 크기 단계. 1 이 기본이다.
const TEXT_SCALE_LIST = [0.9, 1, 1.15, 1.35];
const TEXT_SCALE_LABEL = { 0.9: "작게", 1: "보통", 1.15: "크게", 1.35: "아주 크게" };

const modeOf = (modeKey) =>
  MODE_LIST.find((mode) => mode.key === modeKey) || MODE_LIST[0];

/** 첫 시도 점수들의 평균. 서버의 calculate_grade 와 같은 규칙이다. */
const summarizeFirstAccuracy = (firstAccuracyMap) => {
  const accuracyList = [...firstAccuracyMap.values()];
  if (accuracyList.length === 0) return null;
  const total = accuracyList.reduce((sum, accuracy) => sum + accuracy, 0);
  return {
    score: Math.round(total / accuracyList.length),
    count: accuracyList.length,
  };
};

const splitChunkList = (text) =>
  (text || "")
    .split("/")
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0);

// 영어와 우리말 덩어리를 짝지어 준다. 수가 어긋나면 null 을 돌려 대체 표시로 간다.
const buildChunkPairList = (english, korean) => {
  const englishChunkList = splitChunkList(english);
  const koreanChunkList = splitChunkList(korean);

  if (
    englishChunkList.length === 0 ||
    englishChunkList.length !== koreanChunkList.length
  ) {
    return null;
  }

  return englishChunkList.map((englishChunk, index) => [
    englishChunk,
    koreanChunkList[index],
  ]);
};

// TTS 에는 끊어읽기 표시를 빼고 넘긴다. 슬래시를 그대로 읽어 버리는 목소리가 있다.
const buildSpeechText = (english) =>
  (english || "").replace(/\s*\/\s*/g, " ").trim();

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const DirectTranslationPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section, sectionList, paragraph } = useSelector(
    (state) => state.assignment
  );

  // 같은 지문의 구문분석. **서버를 새로 부르지 않는다.** `section/list` 가 지문의
  // 모든 섹션을 한 번에 내려주므로 여기 이미 들어와 있다.
  //
  // 문장은 자리(index)로 잇는다. 운영 데이터 45개 지문 394문장을 대조해 양쪽 문장
  // 수와 본문이 모두 같은 것을 확인했다. 그래도 어긋나면 그 문장은 그냥 비운다.
  const syntaxItemList = useMemo(() => {
    // **출제할 때 함께 담아 둔 것을 먼저 본다.** 구문분석까지 출제한 지문에서만
    // 단추가 뜨면, 선생님은 왜 어떤 지문에는 없는지 알 수 없다. 서버가 직독직해
    // 섹션에 원본을 같이 넣어 준다(`views.py` 의 `syntax_data`).
    if (section?.question?.syntax_data?.length) {
      return section.question.syntax_data;
    }
    // 그 전에 출제한 과제는 담겨 있지 않다. 형제 섹션이 있으면 그것을 쓴다.
    const syntaxSection = (sectionList || []).find(
      (one) => one.type === "syntax_analysis"
    );
    return syntaxSection?.question?.data || [];
  }, [section, sectionList]);

  // 구문을 곁들여 볼지. **구문분석을 함께 출제한 지문에서만 단추가 뜬다.**
  const [isSyntaxOn, setIsSyntaxOn] = useState(false);
  const { speak, stopSpeaking } = useTTS();
  const {
    isSupported: isSpeechRecognitionSupported,
    listen,
    finishListening,
    stopListening,
  } = useSpeechRecognition();
  const {
    isSupported: isRecorderSupported,
    startRecording,
    stopRecording,
    releaseMicrophone,
  } = useAudioRecorder();

  const [sentenceList, setSentenceList] = useState([]);
  const [wordList, setWordList] = useState([]);
  const [availableModeList, setAvailableModeList] = useState(MODE_LIST);
  const [modeKey, setModeKey] = useState(MODE_LIST[0].key);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);

  // 지금 아이가 무엇을 할 차례인지. 아래 조작 줄에 그대로 띄운다.
  const [turn, setTurn] = useState(null);
  // 지금 무엇을 진하게 보일지. 들려주는 동안은 늘 덩어리 하나다.
  const [highlightScope, setHighlightScope] = useState("chunk");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const isListeningRef = useRef(false);
  // 지금 말소리가 실제로 잡히고 있는지. 음파 표시가 이것으로 커졌다 작아진다.
  const [isSpeechDetected, setIsSpeechDetected] = useState(false);
  // 자리별로 아이가 통역한 말. `모드:문장:덩어리` 를 키로 쓴다.
  const [spokenKoreanMap, setSpokenKoreanMap] = useState({});
  // 자리별 채점 결과. 자리 키 -> {score, isCorrect}. 유창성과 통역 모두 쓴다.
  // 결과 창은 지나가지만 이 자리는 남아서 무엇을 몇 점 받았는지 눈에 남는다.
  const [interpretationScoreMap, setInterpretationScoreMap] = useState({});
  // AI 채점은 몇 초 걸린다. 조용히 돌면 아이는 아무 일도 안 일어난 줄 안다.
  const [isGradingInterpretation, setIsGradingInterpretation] = useState(false);
  // 통역 단계를 끝내고 보여줄 총평. { score, count }
  const [interpretationSummary, setInterpretationSummary] = useState(null);
  // 통역이 기준에 못 미쳐 다시 시킬 때 띄우는 안내. 아무 말 없이 앞으로 돌려보내면
  // 아이는 화면이 튄 줄 안다.
  const [interpretationRetryNotice, setInterpretationRetryNotice] =
    useState(null);
  // 글자 크기. 초등부터 고등까지 쓰는 화면이라 아이가 직접 맞춘다.
  // 고른 값은 이 브라우저에 남겨 다음에 들어와도 그대로다.
  // 듣기 모드에서만 쓰는 두 가지. 아이가 직접 고른다.
  // - 수동: 한 덩어리를 들려주고 멈춰 선다. 따라 말해 보고 스페이스바로 넘긴다.
  // - 표시: 영어 위/우리말 아래가 기본이고, 뒤집으면 우리말을 보고 영어를 떠올린다.
  const [isListeningManual, setIsListeningManual] = useState(false);
  const [listeningLayout, setListeningLayout] = useState("english");

  const [textScale, setTextScale] = useState(() => {
    const saved = Number(localStorage.getItem("directTranslationTextScale"));
    return TEXT_SCALE_LIST.includes(saved) ? saved : 1;
  });

  const handleTextScaleClick = (nextScale) => {
    setTextScale(nextScale);
    localStorage.setItem("directTranslationTextScale", String(nextScale));
  };
  // 방금 읽은 것의 채점 결과. { accuracy, wordsPerMinute, transcript, errorCode }
  const [readingResult, setReadingResult] = useState(null);
  // 통과한 것만 모아 둔다. 다시 읽으면 마지막 것으로 갈아 끼운다.
  const [passedTakeCount, setPassedTakeCount] = useState(0);
  const [isPlayingMyVoice, setIsPlayingMyVoice] = useState(false);
  const [sideTab, setSideTab] = useState("words");
  const [isMobile, setIsMobile] = useState(
    Math.min(window.innerWidth, window.screen.width) <= 768
  );

  const currentSentenceRef = useRef(null);
  // 터치 핸들러가 항상 지금 위치를 보게 한다. 클로저의 옛 값을 보면 엉뚱한
  // 자리부터 다시 시작한다.
  const currentIndexRef = useRef(0);
  const currentChunkIndexRef = useRef(0);
  // 자동 재생 세대. 정지하거나 문장을 옮기면 올려서 돌던 반복을 버린다.
  const playTokenRef = useRef(0);

  // 끝낸 문장 목록. 진도율의 근거라 서버에 그대로 올린다. 재생 루프 안에서
  // 읽고 쓰므로 state 가 아니라 ref 로 들고 있는다(state 로 두면 루프가 붙잡은
  // 옛 값에 덮어써서 앞서 끝낸 것이 지워진다).
  const answerListRef = useRef([]);
  const doneKeySetRef = useRef(new Set());
  const [doneCount, setDoneCount] = useState(0);
  // 지금까지의 첫 시도 평균. 화면에 '직독직해 N점' 으로 띄운다.
  const [scoreSummary, setScoreSummary] = useState(null);
  // 단계별 점수. { fluency: 98, interpretation: 92 }. 하단에 나란히 보여준다.
  // 통역 점수는 채점이 끝난 뒤에 들어오고, 그 뒤로는 계속 남아 있다.
  const [modeScoreMap, setModeScoreMap] = useState({});

  // 이 앱은 로그인이 없다. 주소의 session 에 담긴 학생id:random_code 로 식별한다.
  const [studentId, randomCode] = (() => {
    try {
      const session = new URLSearchParams(window.location.search).get("session");
      return session ? atob(session).split(":") : ["", ""];
    } catch (error) {
      return ["", ""];
    }
  })();

  // **삽화는 섹션이 아니라 지문에 달려 있다.** 한 지문에 한 장이라 섹션마다 같은
  // 주소를 베껴 담을 이유가 없다. 서버가 `Paragraph.illustration_url` 로 주고
  // `ParagraphSerializer` 가 그대로 내려준다. 예전에는 여기서 섹션을 뒤져서 늘
  // 비어 있었다.
  const illustrationUrl =
    paragraph?.illustration_url || section?.question?.illustration_url || null;
  // 아래 셋은 교사가 출제 화면에서 정한다. 학생이 바꾸지 못한다.
  const speechRate = section?.question?.speech_rate || 1;
  // 통과 기준. 0 이면 점수와 상관없이 넘어간다. `||` 를 쓰면 0 이 기본값으로
  // 뒤바뀌므로 값이 없을 때만 기본값을 준다.
  const fluencyPassingScore =
    section?.question?.fluency_passing_score ?? PASSING_ACCURACY;
  const interpretationPassingScore =
    section?.question?.interpretation_passing_score ?? PASSING_ACCURACY;
  const isPronunciationCheckAllowed = Boolean(
    section?.question?.is_pronunciation_check_enabled
  );
  // 교사가 켰고 이 브라우저가 할 수 있으면 켜진 것이다. 학생 쪽 스위치는 없다.
  // 예전에는 권한 오류가 한 번 나면 학생 쪽 스위치를 꺼 버렸는데, 그 뒤로는
  // '읽기' 가 채점 없이 다음으로 넘어가 버렸다.
  const isMicrophoneTurn =
    isPronunciationCheckAllowed &&
    isSpeechRecognitionSupported &&
    modeOf(modeKey).needsMicrophone;
  // 교사는 켰는데 이 브라우저가 못 하는 경우. 조용히 넘어가면 아이는 왜 아무 일도
  // 안 일어나는지 모르고, 원장은 채점이 된 줄 안다.
  const isMicrophoneUnavailable =
    isPronunciationCheckAllowed && !isSpeechRecognitionSupported;
  const isRecordingAllowed = Boolean(section?.question?.is_recording_enabled);
  // 읽는 단위와 따라 읽기는 **교사가 출제할 때 정한다.** 학생 화면에 토글로 두었더니
  // 아이가 무엇을 눌러야 하는지 헷갈려 했다. 아이는 시키는 것만 한다.
  // **기본은 끊어읽기다.** 이 화면의 뜻 자체가 끊어 읽기라, 교사가 일부러
  // '문장 단위' 를 고른 경우에만 문장으로 간다. 예전에는 기본이 문장이라
  // 아무 설정 없이 출제하면 통째로 들려주고 통째로 읽게 했다.
  //
  // **단계마다 단위가 다를 수 있다** (2026-08-27). 유창성은 문장 단위, 통역은
  // 끊어읽기 단위처럼 가르치는 학원이 있어서, reading_unit_by_mode 에 그 단계
  // 값이 있으면 그것을 먼저 본다. 없으면 공통 reading_unit 이다.
  const playUnitOfMode = (targetModeKey) => {
    const byMode = section?.question?.reading_unit_by_mode || {};
    const unit = byMode[targetModeKey] || section?.question?.reading_unit;
    return unit === "sentence" ? "sentence" : "chunk";
  };
  const playUnit = playUnitOfMode(modeKey);

  // **`section` 이 아니라 `section.id` 로 건다.** 진도를 저장할 때마다
  // assignmentSlice 의 updateSection.fulfilled 가 `state.section` 을 서버 응답으로
  // 통째로 갈아끼우므로, `section` 에 걸면 저장할 때마다 이 블록이 다시 돌아
  // 학생을 이어하기 지점으로 되돌려 버린다(문장 6을 읽던 아이가 문장 4로 튄다).
  useEffect(() => {
    if (!section) return;

    setSentenceList(section.question?.data || []);
    setWordList(section.question?.words || []);

    const selectedModeKeyList = section.question?.mode_list || [];
    const nextModeList = selectedModeKeyList.length
      ? MODE_LIST.filter((mode) => selectedModeKeyList.includes(mode.key))
      : MODE_LIST;

    const modeList = nextModeList.length ? nextModeList : MODE_LIST;
    setAvailableModeList(modeList);

    const savedAnswerList = (section.answer || []).filter(
      (answerItem) => answerItem && typeof answerItem === "object"
    );
    answerListRef.current = savedAnswerList;
    doneKeySetRef.current = new Set(
      savedAnswerList.map(
        (answerItem) => `${answerItem.mode}:${answerItem.sentence_index}`
      )
    );
    setDoneCount(savedAnswerList.length);

    firstAccuracyMapRef.current = new Map(
      savedAnswerList
        .filter((answerItem) => typeof answerItem.first_accuracy === "number")
        .map((answerItem) => [
          `${answerItem.mode}:${answerItem.sentence_index}`,
          answerItem.first_accuracy,
        ])
    );
    setScoreSummary(summarizeFirstAccuracy(firstAccuracyMapRef.current));

    // 지난번에 받은 단계별 점수를 되살린다. 다시 들어왔을 때 하단이 비어 있으면
    // 아이는 제가 뭘 받았는지 알 수가 없다.
    const savedModeScoreMap = {};
    savedAnswerList.forEach((answerItem) => {
      if (typeof answerItem.first_accuracy !== "number") return;
      const list = savedModeScoreMap[answerItem.mode] || [];
      list.push(answerItem.first_accuracy);
      savedModeScoreMap[answerItem.mode] = list;
    });
    setModeScoreMap(
      Object.fromEntries(
        Object.entries(savedModeScoreMap).map(([mode, list]) => [
          mode,
          Math.round(list.reduce((sum, one) => sum + one, 0) / list.length),
        ])
      )
    );

    // 하던 데서 이어 간다. 문장 수 × 단계 수라 처음부터 다시 하면 지겹다.
    const sentenceCount = (section.question?.data || []).length;
    const resumePoint = modeList
      .flatMap((mode) =>
        Array.from({ length: sentenceCount }, (unused, index) => [
          mode.key,
          index,
        ])
      )
      .find(([mode, index]) => !doneKeySetRef.current.has(`${mode}:${index}`));

    setModeKey(resumePoint ? resumePoint[0] : modeList[0].key);
    setCurrentIndex(resumePoint ? resumePoint[1] : 0);
    setCurrentChunkIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.id]);

  useEffect(() => {
    const handleResize = () =>
      setIsMobile(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);
  useEffect(() => {
    currentChunkIndexRef.current = currentChunkIndex;
  }, [currentChunkIndex]);

  useEffect(() => {
    if (currentSentenceRef.current) {
      currentSentenceRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [currentIndex]);

  // 아이 차례일 때 재생을 붙잡아 두는 약속. 아이가 스페이스바(모바일은 버튼)를
  // 누르면 풀린다. 시간으로 어림잡아 넘기면 빨리 읽는 아이는 기다리고 느린 아이는
  // 잘린다. 스스로 넘기게 하는 편이 둘 다 맞는다.
  const studentWaitResolveRef = useRef(null);
  // 지금 아이가 읽어야 할 영어. 받아쓴 것과 견주려고 들고 있는다.
  const currentPieceTextRef = useRef("");
  // 통역의 모범 해석. 아이가 말한 것과 함께 AI 에게 보낸다.
  const currentPieceKoreanRef = useRef("");
  // 통역 단계에서 말한 것을 모아 둔다. 단계를 끝낼 때 한 번에 채점한다.
  const interpretationLogRef = useRef([]);
  // 자리마다 아이가 통역한 말. 비어 있던 한글 자리를 이것으로 채운다.
  // 지금 자리의 순서. 녹음을 이어 붙일 때 이 순서대로 놓는다.
  // 지금 아이 차례가 덩어리인지 문장인지. 마이크 상한을 정할 때 쓴다.
  const currentPieceScopeRef = useRef("sentence");
  const currentPieceOrderRef = useRef([0, 0, 0]);
  // 통과한 녹음. 자리마다 하나씩, 마지막 것으로 덮는다.
  const passedTakeMapRef = useRef(new Map());
  // 자리마다 인식이 빈손으로 끝난 횟수. 계속 빈손이면 그 단말은 인식이 안 되는
  // 것이므로 아이를 가두지 않고 보내 준다.
  const emptyListenCountRef = useRef(new Map());
  // 아이가 직접 끝냈는지. 인식이 제멋대로 일찍 끝난 것과 갈라야 한다.
  const userFinishedListeningRef = useRef(false);
  // **지금 들려주는 중인지.** 이 동안에는 마이크를 절대 열지 않는다. 열면 스피커로
  // 나가는 기계 목소리를 그대로 받아써서 만점이 나온다(실제로 그랬다).
  // `speechSynthesis.speaking` 만 보고 판단하면 안 된다. 소리가 끝나기 전에 false 가
  // 되는 단말이 있고, cancel() 뒤에도 꼬리가 잠깐 더 나온다.
  const isSpeakingNowRef = useRef(false);
  // 아이 차례 처리가 도는 중인지. **state 가 아니라 ref 여야 한다.** state 는 다음
  // 그리기까지 값이 안 바뀌어서, 스페이스바를 연타하면 그 틈에 인식이 여러 개
  // 동시에 시작되고 진도와 점수가 엉킨다(연타로 100점이 뜨던 원인).
  const studentTurnBusyRef = useRef(false);
  // 이 화면에서 소리가 한 번이라도 난 적이 있는지. 자동 재생이 막혔을 때만
  // 첫 터치로 살려 주려는 것이라, 한 번 소리가 났으면 그 감시는 끝나야 한다.
  const hasEverSpokenRef = useRef(false);
  // 자리마다 **첫 시도** 점수. 점수의 근거라 뒤에 다시 읽어도 덮지 않는다.
  const firstAccuracyMapRef = useRef(new Map());
  // 마지막으로 읽은 점수와 몇 번 읽었는지. **점수는 첫 시도로 매기지만**(통과할
  // 때까지 다시 읽으므로 마지막으로 매기면 누구나 100점이 된다), 아이를 이만큼
  // 시켰다는 것은 남겨야 선생님이 노력을 본다.
  const lastAccuracyMapRef = useRef(new Map());
  const attemptCountMapRef = useRef(new Map());
  // 방금 읽은 것을 되들려주는 재생기
  const playbackAudioRef = useRef(null);
  // 방금 읽은 조각. 점수 창에서 다시 듣기로 몇 번이고 들을 수 있어야 한다.
  const lastTakeRef = useRef(null);

  const waitForStudent = useCallback(
    () =>
      new Promise((resolve) => {
        studentWaitResolveRef.current = resolve;
      }),
    []
  );

  // 붙잡고 있던 것을 풀어 준다. 풀 것이 있었으면 true.
  const releaseStudentWait = useCallback(() => {
    const resolve = studentWaitResolveRef.current;
    studentWaitResolveRef.current = null;
    if (resolve) resolve();
    return Boolean(resolve);
  }, []);

  const modeIndexOf = useCallback(
    (key) => availableModeList.findIndex((mode) => mode.key === key),
    [availableModeList]
  );

  // 문장 하나를 끝낼 때마다 서버에 올린다. 섹션을 다 끝내야 진도가 오르면
  // 문장 11개 × 단계 3개 동안 0% 로 서 있어서 하다 만 것처럼 보인다.
  const markSentenceDone = useCallback(
    (mode, sentenceIndex) => {
      if (isPreview || isReview || !section) return;

      const doneKey = `${mode}:${sentenceIndex}`;
      if (doneKeySetRef.current.has(doneKey)) return;
      doneKeySetRef.current.add(doneKey);

      // 이 문장에 딸린 자리들의 첫 시도 점수를 평균한다. 끊어읽기 단위면 덩어리가
      // 여럿이라 그 평균이 곧 그 문장의 점수다.
      //
      // **단계까지 봐야 한다.** 문장 번호만 보고 걸렀더니 통역 항목이 유창성에서
      // 받은 점수를 그대로 베껴 갔다(아무것도 안 했는데 통역 100점이 뜬 원인).
      const sentenceModeIndex = modeIndexOf(mode);
      const belongsToSentence = (key) => {
        const [keyModeIndex, keySentenceIndex] = key.split(":").map(Number);
        return (
          keyModeIndex === sentenceModeIndex &&
          keySentenceIndex === sentenceIndex
        );
      };
      const averageOf = (list) =>
        list.length
          ? Math.round(list.reduce((sum, item) => sum + item, 0) / list.length)
          : null;
      const valuesOf = (map) =>
        [...map.entries()].filter(([key]) => belongsToSentence(key)).map(([, one]) => one);

      const sentenceAccuracyList = valuesOf(firstAccuracyMapRef.current);
      const lastAccuracyList = valuesOf(lastAccuracyMapRef.current);
      const attemptCountList = valuesOf(attemptCountMapRef.current);

      const nextAnswerList = [
        ...answerListRef.current,
        {
          mode,
          sentence_index: sentenceIndex,
          is_submitted: true,
          // 채점 없이 낸 과제는 점수가 없다. 서버가 그런 항목을 점수에서 뺀다.
          // 통역은 단계를 끝낸 뒤 AI 가 채점해 따로 채워 넣는다.
          first_accuracy:
            mode === "interpretation" ? null : averageOf(sentenceAccuracyList),
          // 기록용. 점수 산정에는 쓰지 않는다(`calculate_grade.py` 는 first_accuracy 만 본다).
          last_accuracy:
            mode === "interpretation" ? null : averageOf(lastAccuracyList),
          attempt_count: attemptCountList.length
            ? attemptCountList.reduce((sum, one) => sum + one, 0)
            : null,
        },
      ];
      answerListRef.current = nextAnswerList;
      setDoneCount(nextAnswerList.length);

      dispatch(
        updateSection({
          sectionId: section.id,
          data: { answer: nextAnswerList },
        })
      );
    },
    [dispatch, section, isPreview, isReview, modeIndexOf]
  );

  const stopPlaying = useCallback(() => {
    playTokenRef.current += 1;
    stopSpeaking();
    stopListening();
    // 되들려주던 것도 같이 멈춘다. stopPlayback 은 아래에 있어 여기서 직접 끊는다.
    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause();
      playbackAudioRef.current = null;
    }
    releaseStudentWait();
    isListeningRef.current = false;
    studentTurnBusyRef.current = false;
    setIsPlaying(false);
    setIsListening(false);
    setTurn(null);
    setReadingResult(null);
  }, [stopSpeaking, stopListening, releaseStudentWait]);

  /**
   * 아이 차례에 스페이스바나 버튼이 눌렸을 때.
   *
   * 발음 확인이 꺼져 있으면 그냥 넘어간다. 켜져 있으면 여기서 마이크를 연다.
   * **여는 자리가 여기여야 한다.** iOS 는 마이크를 사용자 동작에 묶어 두어서
   * 재생 루프가 스스로 열면 조용히 막힌다.
   */
  // 짝을 못 지은 문장은 통째로 덩어리 하나로 친다. 그래야 슬래시 단위 재생이
  // 그런 문장에서만 멈춰 서지 않는다.
  const chunkPairListOf = useCallback(
    (index) => {
      const sentence = sentenceList[index];
      if (!sentence) return [];
      return (
        buildChunkPairList(sentence[0], sentence[1]) || [
          [sentence[0], sentence[1]],
        ]
      );
    },
    [sentenceList]
  );

  const stopPlayback = useCallback(() => {
    const audio = playbackAudioRef.current;
    playbackAudioRef.current = null;
    if (!audio) return;
    audio.pause();
    if (audio.src) URL.revokeObjectURL(audio.src);
  }, []);

  // 화면을 벗어날 때 소리가 남지 않게 한다. 마이크 표시가 켜진 채 남으면
  // 학생이 계속 녹음되는 줄 안다.
  useEffect(
    () => () => {
      stopPlaying();
      stopPlayback();
      releaseMicrophone();
    },
    [stopPlaying, stopPlayback, releaseMicrophone]
  );

  /** 방금 읽은 것을 들려준다. 끝나면(또는 못 틀면) resolve 한다. */
  const playBackTake = useCallback(
    (blob) =>
      new Promise((resolve) => {
        if (!blob) {
          resolve();
          return;
        }

        const audio = new Audio(URL.createObjectURL(blob));
        playbackAudioRef.current = audio;

        const finish = () => {
          if (playbackAudioRef.current === audio) {
            playbackAudioRef.current = null;
            URL.revokeObjectURL(audio.src);
          }
          resolve();
        };

        audio.onended = finish;
        audio.onerror = finish;
        audio.play().catch(finish);
      }),
    []
  );

  /**
   * 아이 차례에 스페이스바나 버튼이 눌렸을 때.
   *
   * 발음 확인이 꺼져 있으면 그냥 넘어간다. 켜져 있으면 여기서 마이크를 연다.
   * **여는 자리가 여기여야 한다.** iOS 는 마이크를 사용자 동작에 묶어 두어서
   * 재생 루프가 스스로 열면 조용히 막힌다.
   *
   * 받아쓰기와 녹음을 **같은 누름 안에서 둘 다 시작한다.** 하나라도 await 뒤로
   * 미루면 그 사이에 사용자 동작이 끝나 버려 iOS 가 막는다.
   */
  const handleStudentTurnInput = useCallback(async () => {
    // 이 호출이 잠금을 잡았는지. 잡은 호출만 풀어야 한다. 아무나 풀면 듣는 중에
    // 누른 호출이 앞선 것이 아직 도는데도 문을 열어 버린다.
    let hasLock = false;
    try {
      if (!isMicrophoneTurn) {
        releaseStudentWait();
        return;
      }

      // **들려주는 중에는 무시한다.** 스페이스바와 '다음' 버튼이 이 함수로 들어오므로,
      // 버튼을 감추는 것만으로는 막히지 않는다. 여기서 막아야 기계 목소리를 받아쓰고
      // 만점을 주는 일이 없다.
      if (isSpeakingNowRef.current || speechSynthesis.speaking) return;

      // **읽는 중에 누르면 거기가 끝점이다.** 말이 끝난 것을 브라우저가 스스로
      // 알아채는 정확도가 단말마다 들쭉날쭉해서, 아이가 눌러 주는 편이 가장 정확하다.
      // 스스로 끝나면 그것도 받아들인다(먼저 오는 쪽이 이긴다).
      if (isListeningRef.current) {
        userFinishedListeningRef.current = true;
        finishListening();
        return;
      }

      // 이미 처리 중이면 무시한다. 여기가 열려 있으면 연타가 그대로 통과한다.
      if (studentTurnBusyRef.current) return;
      studentTurnBusyRef.current = true;
      hasLock = true;

      const expectedText = currentPieceTextRef.current;
      const pieceOrder = currentPieceOrderRef.current;
      // 통역의 모범 해석도 듣기 시작할 때 붙잡아 둔다. 듣는 사이 자리가 바뀌어도 이 자리의 해석과 견준다.
      const expectedKorean = currentPieceKoreanRef.current;
      setReadingResult(null);
      setIsListening(true);
      isListeningRef.current = true;
      userFinishedListeningRef.current = false;

      // **인식만 돌린다. 녹음은 여기서 하지 않는다.** iOS 는 한 마이크를 인식과
      // 녹음에 같이 주지 않아서, 같이 걸면 인식이 무음만 듣고 채점이 깨진다.
      // 녹음은 별도 경로(녹음 파일을 서버로 보내 전사·채점)로 다시 붙인다.
      const recordingPromise = null;

      // iOS 는 아이가 말하기도 전에 인식을 끝내 버리는 일이 잦다. 아이가 직접
      // 끝내기 전까지는 빈손으로 끝나도 다시 귀를 연다. 녹음이 끝점을 준다.
      let transcript = "";
      let elapsedMilliseconds = 0;
      let errorCode = null;
      // **아이가 끝냈다고 할 때까지 계속 듣는다.** 브라우저가 말하는 중에 제멋대로
      // 끊는 일이 잦은데, 그때 받은 토막만으로 채점하면 잘 읽고도 틀린 것이 된다.
      // 끊기면 다시 열고 받은 것을 이어 붙인다.
      // **마이크는 한 번만 연다.** 여러 번 여닫으면 그때마다 iOS 가 '띠링' 소리를
      // 내고, 이어 듣기(continuous)를 켜면 말이 끝나도 스스로 안 끝나 채점이 한참
      // 늦어진다. 한 번 듣고 그것으로 채점한다.
      //
      // 다만 **입도 떼기 전에 빈손으로 끝난 것은 채점하지 않는다.** iOS 가 마이크를
      // 열자마자 끝내 버리는 일이 잦은데, 그것을 결과로 받으면 시작하자마자 0점이
      // 된다. 그때만 조용히 한 번 더 연다.
      let hasHeardVoice = false;
      const noticeVoice = (isActive) => {
        if (isActive) hasHeardVoice = true;
        setIsSpeechDetected(isActive);
      };

      // 마이크를 열어 두는 상한. **문장 길이에 맞춰 준다.** 예전에는 어느 문장이든
      // 10초여서, 통역으로 긴 문장을 옮길 때 말하는 중에 끊겼다.
      //
      // 통역의 4배는 **문장 단위**의 값이다. 문장은 다 듣고 머릿속에서 통째로
      // 재구성해야 해서 길게 준다. 끊어읽기는 방금 들은 덩어리 하나만 옮기면
      // 되므로 4배를 주면 침묵이 너무 길다. 2.5배면 된다.
      const listenRatio =
        currentPieceScopeRef.current === "chunk"
          ? STUDENT_THINKING_RATIO
          : modeOf(modeKey).listenRatio || STUDENT_THINKING_RATIO;
      const listenMilliseconds = Math.min(
        MAXIMUM_LISTEN_MILLISECONDS,
        Math.max(
          MINIMUM_LISTEN_MILLISECONDS,
          estimateSpeechMilliseconds(expectedText) * listenRatio
        )
      );

      for (let attempt = 0; attempt < 3; attempt++) {
        const result = await listen({
          language: modeOf(modeKey).language || "en-US",
          onSpeechActivity: noticeVoice,
          maximumMilliseconds: listenMilliseconds,
        });
        transcript = result.transcript;
        elapsedMilliseconds += result.elapsedMilliseconds;
        errorCode = result.errorCode;

        if (transcript) break;
        if (hasHeardVoice) break;
        if (userFinishedListeningRef.current) break;
        if (
          errorCode === "not-allowed" ||
          errorCode === "service-not-allowed" ||
          errorCode === "unsupported"
        )
          break;
      }

      const takeBlob =
        recordingPromise && (await recordingPromise) ? await stopRecording() : null;

      setIsListening(false);
      isListeningRef.current = false;
      setIsSpeechDetected(false);

      if (errorCode === "not-allowed" || errorCode === "service-not-allowed") {
        setReadingResult(null);
        // **마이크가 막혔다고 아이를 가두지 않는다.** 예전에는 안내만 띄우고
        // 돌아가서, 그 문장이 진도로도 안 남고 다음 문장으로도 못 갔다. 아이는
        // 거기서 멈췄고 과제는 영영 100% 가 되지 않았다(구시아 학생 94%).
        // 넘어간 것으로 치고 이어 간다. 마이크는 설정을 고친 뒤 다시 하면 된다.
        markSentenceDone(modeKey, currentPieceOrderRef.current[1]);
        releaseStudentWait();
        // **어디를 눌러야 하는지까지 적어 준다.** 그냥 '마이크를 허용해 주세요' 라고만
        // 하면 아이폰 쓰는 아이는 찾지 못한다. 아이폰은 사파리 권한만으로는 안 되고
        // '받아쓰기' 가 켜져 있어야 인식이 돌아간다(꺼져 있으면 여기로 떨어진다).
        alert(
          "마이크를 쓸 수 없어요.\n\n" +
            "아이폰\n" +
            "1. 설정 > 일반 > 키보드 > '받아쓰기 활성화' 켜기\n" +
            "2. 설정 > Safari > 마이크 > '허용'\n" +
            "3. 이 화면을 새로고침한 뒤 다시 눌러 주세요\n\n" +
            "안드로이드\n" +
            "1. 주소창 왼쪽 자물쇠 > 권한 > 마이크 허용\n" +
            "2. 권한 목록에 마이크가 없으면: 휴대폰 설정 > 애플리케이션 >\n" +
            "   지금 쓰는 브라우저(Chrome 등) > 권한 > 마이크 허용\n" +
            "3. 이 화면을 새로고침한 뒤 다시 눌러 주세요\n\n" +
            "그래도 안 되면 '건너뛰기' 로 넘어갈 수 있어요."
        );
        return;
      }

      // 인식이 빈손으로 끝났을 때. iOS 는 아이가 말하기도 전에 끝내 버리는 일이
      // 잦다. **이것을 통과로 치면 누르자마자 다음으로 넘어가 버린다**(실제로 그랬다).
      // 통과로도 0점으로도 치지 않고 같은 자리를 다시 시킨다. 다만 같은 자리에서
      // 세 번을 빈손이면 그 단말은 인식이 안 되는 것이니 가두지 않고 보내 준다.
      const pieceKey = pieceOrder.join(":");

      let isScoringUnavailable = false;
      if (!transcript) {
        const emptyCount = (emptyListenCountRef.current.get(pieceKey) || 0) + 1;
        emptyListenCountRef.current.set(pieceKey, emptyCount);
        isScoringUnavailable = emptyCount >= 3;

        if (!isScoringUnavailable) {
          // 창을 띄우지 않는다. 소리를 못 들은 것은 아이 잘못이 아닌데 "틀렸다" 는
          // 인상만 준다. 아이 차례를 그대로 두어 한 번 더 누르면 다시 듣는다.
          setReadingResult(null);
          return;
        }
      } else {
        emptyListenCountRef.current.delete(pieceKey);
      }

      // 통역은 우리말이라 단어 겹치기로 못 센다. 말한 것만 모아 두고, 이 단계를
      // 다 끝냈을 때 한 번에 AI 에게 보내 채점한다(문장마다 부르면 값이 크게 붙는다).
      if (modeKey === "interpretation") {
        const koreanAnswer = expectedKorean;
        interpretationLogRef.current.push({
          index: interpretationLogRef.current.length,
          // 채점 결과를 어느 자리로 되돌릴지. 서버는 index 만 돌려주므로 우리가 짝을
          // 들고 있어야 한다.
          pieceKey: pieceOrder.join(":"),
          expected: koreanAnswer,
          spoken: transcript,
        });

        // 비어 있던 우리말 자리를 아이가 말한 것으로 채운다. 모범 해석이 아니라
        // **제가 한 말**이 그 자리에 남아야 무엇을 어떻게 옮겼는지 눈에 남는다.
        setSpokenKoreanMap((previous) => ({
          ...previous,
          [pieceOrder.join(":")]: transcript,
        }));

        // **결과 창을 띄우지 않는다.** 말한 것은 이미 위 본문의 그 자리에 적혔으므로
        // 창은 같은 말을 한 번 더 보여주며 흐름만 끊는다. 곧바로 다음으로 넘어간다.
        // 못 알아들었을 때만 알려 주고 그 자리를 다시 시킨다.
        if (!transcript) return;

        releaseStudentWait();
        return;
      }

      const accuracy = calculateReadingAccuracy(expectedText, transcript);
      const isPassed = isScoringUnavailable || accuracy >= fluencyPassingScore;

      // 읽을 때마다 마지막 점수와 횟수를 갱신한다. 점수 산정에는 안 쓰고 기록용이다.
      if (!isScoringUnavailable) {
        lastAccuracyMapRef.current.set(pieceKey, accuracy);
        attemptCountMapRef.current.set(
          pieceKey,
          (attemptCountMapRef.current.get(pieceKey) || 0) + 1
        );
      }

      // 첫 시도만 점수로 남긴다. 통과할 때까지 다시 읽게 되어 있어서 마지막 것으로
      // 매기면 누구나 100점이 된다. 채점을 못 한 것은 점수에 넣지 않는다.
      if (!isScoringUnavailable && !firstAccuracyMapRef.current.has(pieceKey)) {
        firstAccuracyMapRef.current.set(pieceKey, accuracy);
        setScoreSummary(summarizeFirstAccuracy(firstAccuracyMapRef.current));

        // 이 단계(유창성)의 평균만 따로 낸다. 첫 시도 기준이라 다시 읽어도 안 바뀐다.
        const modeIndex = modeIndexOf(modeKey);
        const stageScoreList = [...firstAccuracyMapRef.current.entries()]
          .filter(([key]) => key.startsWith(`${modeIndex}:`))
          .map(([, one]) => one);
        if (stageScoreList.length > 0) {
          setModeScoreMap((previous) => ({
            ...previous,
            [modeKey]: Math.round(
              stageScoreList.reduce((sum, one) => sum + one, 0) /
                stageScoreList.length
            ),
          }));
        }
      }

      // 통과한 것만 모은다. 같은 자리를 다시 읽으면 마지막 것으로 갈아 끼운다.
      if (isPassed && takeBlob) {
        passedTakeMapRef.current.set(pieceOrder.join(":"), {
          order: pieceOrder,
          blob: takeBlob,
        });
        setPassedTakeCount(passedTakeMapRef.current.size);
      }

      setReadingResult({
        accuracy,
        wordsPerMinute: calculateWordsPerMinute(transcript, elapsedMilliseconds),
        transcript,
        errorCode,
        hasTake: Boolean(takeBlob),
        isScoringUnavailable,
      });

      lastTakeRef.current = takeBlob;

      // 점수만 보여주고 넘어가면 어디가 틀렸는지 알 수 없다. 제 목소리를 들어봐야
      // 안다. 다 들은 뒤에 창을 닫는다.
      if (takeBlob) {
        await playBackTake(takeBlob);
      } else {
        await wait(RESULT_SHOW_MILLISECONDS);
      }

      if (isPassed) {
        setReadingResult(null);
        releaseStudentWait();
        return;
      }

      // 틀렸으면 **다시 들려주고 그 자리에서 기다린다.** 아이는 한 번 더 누르기만
      // 하면 된다. 들려주는 동안은 '듣는 차례' 로 바꿔 🎙 버튼을 접는다. 열어 두면
      // TTS 가 나오는 중에 녹음이 걸려 TTS 소리를 받아쓰게 된다(실제로 그랬다).
      setReadingResult(null);
      setTurn("listen");
      isSpeakingNowRef.current = true;
      await speak({ text: expectedText, rate: speechRate });
      await wait(SPEECH_TAIL_MILLISECONDS);
      isSpeakingNowRef.current = false;
      setTurn("repeat");
    } finally {
      if (hasLock) studentTurnBusyRef.current = false;
    }
  }, [
    isMicrophoneTurn,
    modeKey,
    isListening,
    listen,
    finishListening,
    releaseStudentWait,
    speak,
    stopSpeaking,
    speechRate,
    stopRecording,
    playBackTake,
    // 통과 기준이 빠져 있으면 이 콜백이 처음 만들어질 때의 값을 계속 붙든다.
    // 출제에서 기준을 바꿔도 아이 화면은 옛 기준으로 판정한다.
    fluencyPassingScore,
    modeIndexOf,
    // 마이크가 막혔을 때 그 문장을 진도로 남기고 넘어간다.
    markSentenceDone,
  ]);

  /** 한 토막을 들려준다. 그 덩어리만 진하게 켠다.
   *
   * iOS 는 사용자 동작 밖의 TTS 를 소리 없이 무시할 수 있다. 그대로 상한 타이머로
   * 넘어가면 **소리 없이 다음 문장으로 굴러가는 유령 재생**이 된다. 1.2초 안에
   * 실제로 소리가 나기 시작했는지 보고, 안 났으면 재생을 세운다. 다음 사용자
   * 동작(터치·버튼)에서 다시 시작하면 그때는 소리가 난다.
   */
  const speakPiece = useCallback(
    async (englishText, token) => {
      setHighlightScope("chunk");
      setTurn("listen");
      isSpeakingNowRef.current = true;

      const speakPromise = speak({ text: englishText, rate: speechRate });

      let hasStartedSpeaking = false;
      for (let i = 0; i < 12; i++) {
        await wait(100);
        if (speechSynthesis.speaking) {
          hasStartedSpeaking = true;
          break;
        }
      }

      if (hasStartedSpeaking) hasEverSpokenRef.current = true;

      if (!hasStartedSpeaking) {
        // 소리가 시작되지 않았다. 유령으로 굴러가지 말고 여기서 선다.
        stopSpeaking();
        isSpeakingNowRef.current = false;
        return false;
      }

      await Promise.race([
        speakPromise,
        wait(estimateSpeechMilliseconds(englishText) * 2 + 3000),
      ]);

      // 소리의 꼬리가 마이크로 새지 않게 잠깐 더 잠근다.
      await wait(SPEECH_TAIL_MILLISECONDS);
      isSpeakingNowRef.current = false;
      return token === playTokenRef.current;
    },
    [speak, speechRate, stopSpeaking]
  );

  /**
   * 아이가 따라 읽을 차례. 아이가 누를 때까지 여기서 선다.
   *
   * `scope` 는 무엇을 읽을 차례인지다. 문장 단위면 문장 전체가 진해지고,
   * 끊어읽기 단위면 방금 들려준 덩어리만 진한 채로 남는다.
   */
  const waitForStudentTurn = useCallback(
    async (englishText, order, token, scope, koreanText = "") => {
      currentPieceTextRef.current = englishText;
      currentPieceKoreanRef.current = koreanText;
      currentPieceOrderRef.current = order;
      currentPieceScopeRef.current = scope;
      setHighlightScope(scope);
      setTurn("repeat");
      await waitForStudent();
      setTurn(null);
      return token === playTokenRef.current;
    },
    [waitForStudent]
  );

  const isManualListening =
    modeKey === "listening" && isListeningManual;

  const playFrom = useCallback(
    async (startIndex, startChunkIndex = 0) => {
      const token = playTokenRef.current + 1;
      playTokenRef.current = token;
      setIsPlaying(true);

      const modeIndex = availableModeList.findIndex(
        (mode) => mode.key === modeKey
      );

      for (let index = startIndex; index < sentenceList.length; index++) {
        if (token !== playTokenRef.current) return;

        const chunkPairList = chunkPairListOf(index);
        const firstChunk = index === startIndex ? startChunkIndex : 0;

        // **들려주는 것은 언제나 덩어리씩이다.** 문장을 통째로 읽어 주면 어디를
        // 듣고 있는지 따라갈 수가 없다. 교사가 고른 단위는 '아이가 따라 읽는 단위'
        // 이지 '들려주는 단위' 가 아니다.
        for (
          let chunkIndex = firstChunk;
          chunkIndex < chunkPairList.length;
          chunkIndex++
        ) {
          if (token !== playTokenRef.current) return;

          setCurrentIndex(index);
          setCurrentChunkIndex(chunkIndex);

          const chunkText = chunkPairList[chunkIndex][0];
          const order = [modeIndex, index, chunkIndex];

          // **'해석+영어' 는 순서가 뒤집힌다.** 우리말을 보고 아이가 먼저 영어를
          // 떠올려 말한 뒤, 그다음에 들려줘야 확인이 된다. 먼저 들려주면 떠올릴
          // 것이 없다. 지금 덩어리의 영어를 감춰 둔 것도 같은 이유다.
          const isSpeakAfterStudent =
            modeKey === "listening" && listeningLayout === "korean";

          if (playUnit === "chunk" && isSpeakAfterStudent) {
            setHighlightScope("chunk");
            setTurn("repeat");
            if (isManualListening) {
              await waitForStudent();
            } else {
              // 자동일 때도 떠올릴 틈은 준다. 곧바로 들려주면 소용이 없다.
              // **읽는 시간의 2.5배를 준다.** 아이는 우리말을 읽고 영어를 떠올린
              // 뒤에야 입을 뗀다. 읽는 시간만 주면 생각할 틈이 없다.
              await wait(
                estimateSpeechMilliseconds(chunkText) * STUDENT_THINKING_RATIO
              );
            }
            setTurn(null);
            if (token !== playTokenRef.current) return;
          }

          if (!(await speakPiece(chunkText, token))) {
            // 토큰이 그대로면 소리가 막혀 선 것이다. 재생 표시를 걷어 아이가
            // 다시 시작할 수 있게 한다(화면 아무 데나 터치).
            if (token === playTokenRef.current) {
              setIsPlaying(false);
              setTurn(null);
            }
            return;
          }

          // 듣기 모드를 수동으로 두면 마이크 없이도 아이 차례를 만든다. 따라 말해
          // 보고 스페이스바(모바일은 버튼)로 넘긴다. 자동으로 흘러가면 입을 뗄
          // 틈이 없다. 위에서 이미 아이 차례를 준 경우는 건너뛴다.
          if (playUnit === "chunk" && isManualListening && !isSpeakAfterStudent) {
            setHighlightScope("chunk");
            setTurn("repeat");
            await waitForStudent();
            setTurn(null);
            if (token !== playTokenRef.current) return;
          }

          if (playUnit === "chunk" && modeOf(modeKey).needsMicrophone) {
            if (
              !(await waitForStudentTurn(
                chunkText,
                order,
                token,
                "chunk",
                chunkPairList[chunkIndex][1]
              ))
            )
              return;
          }

          await wait(CHUNK_GAP_MILLISECONDS);
          if (token !== playTokenRef.current) return;
        }

        // 문장 단위면 덩어리를 다 들려준 뒤에 문장 하나를 통째로 읽게 한다.
        if (playUnit === "sentence") {
          const sentenceText = buildSpeechText(sentenceList[index][0]);
          const order = [modeIndex, index, 0];

          // 마이크를 안 쓰는 듣기 모드라도 수동이면 아이 차례를 준다. 예전에는
          // 이 자리가 끊어읽기 단위에만 있어서, **문장 단위 듣기는 아이를 기다리지
          // 않고 그냥 다음 문장으로 흘러갔다**(스페이스바가 먹지 않던 이유).
          if (isManualListening && !modeOf(modeKey).needsMicrophone) {
            setHighlightScope("sentence");
            setTurn("repeat");
            await waitForStudent();
            setTurn(null);
            if (token !== playTokenRef.current) return;
          }

          if (
            modeOf(modeKey).needsMicrophone &&
            !(await waitForStudentTurn(
              sentenceText,
              order,
              token,
              "sentence",
              sentenceList[index][1]
            ))
          ) {
            return;
          }
        }

        markSentenceDone(modeKey, index);
        await wait(SENTENCE_GAP_MILLISECONDS);
      }

      if (token === playTokenRef.current) {
        setIsPlaying(false);
        setTurn(null);
      }
    },
    [
      sentenceList,
      playUnit,
      chunkPairListOf,
      speakPiece,
      waitForStudentTurn,
      waitForStudent,
      markSentenceDone,
      modeKey,
      availableModeList,
      isManualListening,
      listeningLayout,
      waitForStudent,
    ]
  );

  // **들어오면 바로 읽어준다.** 아이가 '듣기' 를 찾아 누를 이유가 없다.
  // iOS 는 사용자 동작 밖의 소리를 막을 수 있어서, 시작하고 잠깐 뒤에도 소리가
  // 안 났으면 첫 터치(아무 데나)에 다시 시작한다.
  const hasAutoStartedRef = useRef(false);
  useEffect(() => {
    if (hasAutoStartedRef.current) return;
    if (!section || sentenceList.length === 0) return;
    hasAutoStartedRef.current = true;
    playFrom(currentIndex, currentChunkIndex);

    const handleFirstTouch = (event) => {
      // **소리가 한 번이라도 났으면 이 감시는 할 일이 없다.** 자동 재생이 막힌
      // 경우만 살려 주는 장치다. 계속 켜 두었더니 학습 도중의 빈 곳 터치가
      // 재생을 죽이고 문장 처음부터 다시 돌려 버렸다.
      if (hasEverSpokenRef.current) return;
      if (speechSynthesis.speaking || studentWaitResolveRef.current) return;
      if (isListeningRef.current) return;
      // 버튼을 누른 것이면 그 버튼이 알아서 한다. 여기서 겹치면 두 번 돈다.
      if (event.target.closest("button")) return;
      stopPlaying();
      playFrom(currentIndexRef.current, currentChunkIndexRef.current);
    };
    // once 로 두지 않는다. 유령 감시가 재생을 세울 때마다 다음 터치가 다시 살린다.
    window.addEventListener("pointerdown", handleFirstTouch);
    return () =>
      window.removeEventListener("pointerdown", handleFirstTouch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.id, sentenceList.length]);

  const handlePlayClick = useCallback(() => {
    if (isPlaying) {
      stopPlaying();
      return;
    }
    // 듣는 중이거나 결과가 떠 있으면 지금 흐름이 살아 있다. 새 루프를 걸면
    // 둘이 같이 돌면서 진도와 점수가 뒤엉킨다.
    if (isListening || readingResult) return;
    playFrom(currentIndex, currentChunkIndex);
  }, [
    isPlaying,
    isListening,
    readingResult,
    stopPlaying,
    playFrom,
    currentIndex,
    currentChunkIndex,
  ]);

  // 스페이스바로 넘긴다. 아이 차례면 그 자리를 풀어 주고, 아니면 재생을 켜고 끈다.
  // 모바일에는 스페이스바가 없으므로 아래 '다 읽었어요' 버튼이 같은 일을 한다.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " ") return;

      const tagName = event.target?.tagName;
      if (tagName === "INPUT" || tagName === "TEXTAREA") return;

      // 버튼에 초점이 있으면 스페이스바가 그 버튼도 누른다. 두 번 도는 것을 막는다.
      event.preventDefault();

      // 아이 차례인지 여기서 바로 가른다. handleStudentTurnInput 은 비동기라
      // 그 결과를 기다렸다가 가르면 그 사이에 재생 토글까지 눌린다.
      if (studentWaitResolveRef.current) {
        handleStudentTurnInput();
        return;
      }

      // **붙잡고 있는 것이 없으면 아무 것도 하지 않는다.**
      //
      // 한때 여기서 '다음' 이나 '읽기' 를 부르게 해 봤는데, 재생 루프가 도는 중에도
      // 발동해서 들려주는 도중에 읽기가 시작되거나 문장이 건너뛰어졌다. 아이 차례는
      // 재생 루프가 만들고, 스페이스바는 그것을 풀어 주기만 한다.
      //
      // 재생을 켜고 끄는 데도 쓰지 않는다. 예전에 그렇게 두었더니 연타에 루프가
      // 여러 개 돌면서 점수와 진도가 뒤엉켰다. 재생은 '듣기' 버튼이 한다.
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleStudentTurnInput]);

  // 발음이 안 맞아도 아이를 붙잡아 두지 않는다. 받아쓰기가 못 알아듣는 경우가 있어
  // 빠져나갈 길이 없으면 거기서 과제가 끝난다.
  const handleSkipReadingClick = () => {
    stopPlayback();
    setReadingResult(null);
    // **건너뛴 것도 진도로 센다.** 안 그러면 발음 확인이 막힌 아이는 그 단계를
    // 끝낼 수가 없어 과제가 100% 가 되지 않는다. 건너뛰기를 둔 뜻이 사라진다.
    markSentenceDone(modeKey, currentIndex);
    releaseStudentWait();
  };

  /**
   * 아이가 읽은 것을 처음부터 다시 들려준다.
   *
   * 파일 하나로 이어 붙이지 않는다. 조각을 순서대로 틀면서 그 자리의 덩어리를
   * 켜 주면, TTS 로 들을 때와 화면이 똑같이 움직여 어디를 듣고 있는지 보인다.
   * 굽는 시간을 기다릴 일도 없다.
   */
  const playMyVoiceThrough = useCallback(async () => {
    const takeList = [...passedTakeMapRef.current.values()].sort(
      (left, right) =>
        left.order[0] - right.order[0] ||
        left.order[1] - right.order[1] ||
        left.order[2] - right.order[2]
    );
    if (takeList.length === 0) return;

    const token = playTokenRef.current + 1;
    playTokenRef.current = token;
    setIsPlayingMyVoice(true);
    setHighlightScope("chunk");

    for (const take of takeList) {
      if (token !== playTokenRef.current) break;
      const [, sentenceIndex, chunkIndex] = take.order;
      setCurrentIndex(sentenceIndex);
      setCurrentChunkIndex(chunkIndex);
      await playBackTake(take.blob);
      if (token !== playTokenRef.current) break;
      await wait(CHUNK_GAP_MILLISECONDS);
    }

    if (token === playTokenRef.current) setIsPlayingMyVoice(false);
  }, [playBackTake]);

  /**
   * 통역 단계에서 말한 것을 한 번에 채점한다.
   *
   * 문장마다 부르지 않는다. 지문 하나에 수십 번이 되어 값이 크게 붙고 느리다.
   * 채점이 실패해도 학습은 이미 끝난 것이므로 막지 않고 넘어간다.
   */
  const gradeInterpretationLog = useCallback(async () => {
    const itemList = interpretationLogRef.current;
    if (itemList.length === 0) return;
    interpretationLogRef.current = [];

    if (isPreview || isReview || !studentId || !randomCode) return;

    setIsGradingInterpretation(true);
    try {
      const { result_list: resultList } = await gradeInterpretationAPI({
        student_id: studentId,
        random_code: randomCode,
        // pieceKey 는 우리끼리 쓰는 것이라 서버로 보내지 않는다.
        item_list: itemList.map(({ index, expected, spoken }) => ({
          index,
          expected,
          spoken,
        })),
      });

      const nextScoreMap = {};
      (resultList || []).forEach((result) => {
        const score = Number(result.score);
        if (Number.isNaN(score)) return;

        const clamped = Math.max(0, Math.min(100, score));
        firstAccuracyMapRef.current.set(
          `interpretation:${result.index}`,
          clamped
        );

        const item = itemList.find((one) => one.index === result.index);
        if (item) {
          nextScoreMap[item.pieceKey] = {
            score: clamped,
            isCorrect: Boolean(result.is_correct),
            // 아이가 말한 우리말. 해설지에서 학부모가 이것을 본다.
            // **영어 읽기(유창성)의 받아쓴 것은 남기지 않는다.** 점수와 시도
            // 횟수만으로 충분해서, 굳이 아이 말을 더 쌓아 둘 이유가 없다.
            spoken: item.spoken || "",
          };
        }
      });

      setInterpretationScoreMap((previous) => ({
        ...previous,
        ...nextScoreMap,
      }));
      setScoreSummary(summarizeFirstAccuracy(firstAccuracyMapRef.current));

      // 총평을 띄운다. 아이가 이 단계에서 무엇을 받았는지 한 번은 보고 넘어가야 한다.
      const scoreList = Object.values(nextScoreMap).map((one) => one.score);
      if (scoreList.length > 0) {
        const averageScore = Math.round(
          scoreList.reduce((sum, one) => sum + one, 0) / scoreList.length
        );
        setInterpretationSummary({ score: averageScore, count: scoreList.length });
        // 하단에 남겨 둔다. 다시 들어와도 이 점수가 보여야 한다.
        setModeScoreMap((previous) => ({
          ...previous,
          interpretation: averageScore,
        }));

        // **서버에도 남긴다.** 화면에만 두면 새로고침 한 번에 사라지고, 교사도 못 본다.
        // 통역은 AI 가 채점하므로 읽기와 달리 이 자리에서만 점수를 알 수 있다.
        // **첫 덩어리만 보면 안 된다.** 끊어읽기 단위면 한 문장에 덩어리가 여럿이라,
        // chunk 0 만 집으면 아이가 덩어리 셋을 다 통역해도 첫 것의 점수와 말만
        // 남았다(해설지에 첫 조각만 찍히던 원인). 그 문장의 덩어리를 전부 모아
        // 점수는 평균, 말은 순서대로 이어 붙인다.
        const interpretationModeIndex = modeIndexOf("interpretation");
        const gradedAnswerList = answerListRef.current.map((answerItem) => {
          if (answerItem.mode !== "interpretation") return answerItem;
          const gradedPieceList = Object.entries(nextScoreMap)
            .map(([pieceKey, graded]) => ({
              order: pieceKey.split(":").map(Number),
              graded,
            }))
            .filter(
              ({ order }) =>
                order[0] === interpretationModeIndex &&
                order[1] === answerItem.sentence_index
            )
            .sort((a, b) => a.order[2] - b.order[2]);
          if (gradedPieceList.length === 0) return answerItem;
          const scoreTotal = gradedPieceList.reduce(
            (sum, { graded }) => sum + graded.score,
            0
          );
          return {
            ...answerItem,
            first_accuracy: Math.round(scoreTotal / gradedPieceList.length),
            spoken: gradedPieceList
              .map(({ graded }) => graded.spoken)
              .filter(Boolean)
              .join(" / "),
          };
        });
        answerListRef.current = gradedAnswerList;

        if (section) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { answer: gradedAnswerList },
            })
          );
        }
      }
      // 기준에 못 미친 문장을 돌려준다. 부르는 쪽이 다시 시킬지 정한다.
      return itemList
        .filter((item) => {
          const graded = nextScoreMap[item.pieceKey];
          return graded && graded.score < interpretationPassingScore;
        })
        .map((item) => Number(item.pieceKey.split(":")[1]))
        .filter((index, position, list) => list.indexOf(index) === position);
    } catch (error) {
      // 채점이 안 돼도 아이가 한 것은 남는다. 여기서 막지 않는다.
    } finally {
      setIsGradingInterpretation(false);
    }

    return [];
  }, [
    isPreview,
    isReview,
    studentId,
    randomCode,
    modeIndexOf,
    dispatch,
    section,
    interpretationPassingScore,
  ]);

  const handleModeClick = (nextModeKey) => {
    stopPlaying();
    gradeInterpretationLog();
    setModeKey(nextModeKey);
    setCurrentIndex(0);
    setCurrentChunkIndex(0);
  };

  /**
   * 문장(또는 덩어리)을 눌렀을 때.
   *
   * **아이 차례면 누르는 것이 곧 녹음 시작이다.** 모바일에는 스페이스바가 없고,
   * 아래 버튼까지 손을 내리는 것보다 지금 읽는 글을 누르는 편이 자연스럽다.
   * 마이크를 여는 자리도 여기여야 iOS 가 막지 않는다.
   */
  const handlePieceClick = (index, chunkIndex) => {
    if (studentWaitResolveRef.current) {
      handleStudentTurnInput();
      return;
    }

    // 재생 중이거나 채점 결과가 떠 있으면 아무 것도 하지 않는다. 여기서 다시
    // 읽어 주면 방금 읽은 덩어리가 또 나온다(문장 클릭은 늘 첫 덩어리를 가리킨다).
    if (isPlaying || isListening || readingResult) return;

    stopPlaying();
    setCurrentIndex(index);
    setCurrentChunkIndex(chunkIndex);
    setHighlightScope("chunk");
    speak({
      text: chunkPairListOf(index)[chunkIndex]?.[0] || "",
      rate: speechRate,
    });
  };

  const handleSentenceClick = (index) => handlePieceClick(index, 0);
  const handleChunkClick = (index, chunkIndex) =>
    handlePieceClick(index, chunkIndex);

  /**
   * 아이 차례가 아닐 때도 지금 자리를 그냥 읽어볼 수 있게 한다.
   *
   * 녹음은 '듣기' 로 재생을 시작해 아이 차례가 와야만 열렸다. 그래서 화면 어디에도
   * 녹음이 있다는 표가 없었고, 눌러볼 방법도 없었다.
   */
  const handleRecordNowClick = async () => {
    if (studentWaitResolveRef.current) {
      handleStudentTurnInput();
      return;
    }

    stopPlaying();

    const modeIndex = availableModeList.findIndex(
      (mode) => mode.key === modeKey
    );
    const chunkPairList = chunkPairListOf(currentIndex);
    const chunkIndex = playUnit === "chunk" ? currentChunkIndex : 0;

    currentPieceTextRef.current =
      playUnit === "chunk"
        ? chunkPairList[chunkIndex]?.[0] || ""
        : buildSpeechText(sentenceList[currentIndex]?.[0] || "");
    // **모범 해석도 지금 자리로 바꾼다.** 예전에는 영어와 자리만 바꾸고 해석은 마지막으로 재생된 문장 것이
    // 남아서, 이 단추로 한 통역은 엉뚱한 문장과 견줘 거의 0점이었다(2026-10-18, 8월부터 여러 학원).
    currentPieceKoreanRef.current =
      playUnit === "chunk"
        ? chunkPairList[chunkIndex]?.[1] || ""
        : sentenceList[currentIndex]?.[1] || "";
    currentPieceOrderRef.current = [modeIndex, currentIndex, chunkIndex];

    setHighlightScope(playUnit === "chunk" ? "chunk" : "sentence");
    setTurn("repeat");
    await handleStudentTurnInput();
    setTurn(null);
  };

  const finishSection = async () => {
    await gradeInterpretationLog();

    // 빠진 자리가 있으면 완료로 치지 않는다. 마지막 문장까지 왔어도 중간에 튕겼거나
    // 건너뛴 자리가 있으면 서버 진도는 97% 인데 완료 표시만 켜져, "학생 화면은 다
    // 초록인데 교사 화면은 97%" 로 어긋났다 (2026-08-26 임주혜). 첫 빈자리로
    // 돌려보내 마저 하게 한다.
    const sentenceTotal = (section?.question?.data || []).length;
    const missingPoint = availableModeList
      .flatMap((mode) =>
        Array.from({ length: sentenceTotal }, (unused, index) => [
          mode.key,
          index,
        ])
      )
      .find(([mode, index]) => !doneKeySetRef.current.has(`${mode}:${index}`));
    if (missingPoint) {
      const [missingModeKey, missingIndex] = missingPoint;
      alert(
        `${modeOf(missingModeKey).value} 단계의 ${missingIndex + 1}번 문장이 ` +
          "아직 남아 있어요. 그 문장으로 이동합니다."
      );
      setModeKey(missingModeKey);
      setCurrentIndex(missingIndex);
      setCurrentChunkIndex(0);
      return;
    }

    // 다 읽었으면 제 목소리로 한 번 훑어 준다. 버튼을 따로 두지 않는다.
    await playMyVoiceThrough();

    if (!isPreview && !isReview) {
      dispatch(
        updateSection({ sectionId: section.id, data: { is_finished: true } })
      );
    }
    alert(
      scoreSummary
        ? `학습이 완료되었습니다.\n직독직해 ${scoreSummary.score}점 (처음 읽은 것 기준)`
        : "학습이 완료되었습니다."
    );
    setCurrentStep(2);
  };

  const handleNextClick = async () => {
    // 아이 차례면 재생을 끊지 않고 그대로 이어 간다. 모바일에서 스페이스바 대신
    // 누르는 버튼이 이것이다.
    if (studentWaitResolveRef.current) {
      handleStudentTurnInput();
      return;
    }

    stopPlaying();

    if (
      playUnit === "chunk" &&
      currentChunkIndex < chunkPairListOf(currentIndex).length - 1
    ) {
      setCurrentChunkIndex(currentChunkIndex + 1);
      return;
    }

    // 여기까지 왔으면 이 문장은 끝난 것이다. 손으로 넘겨도 진도에 들어간다.
    markSentenceDone(modeKey, currentIndex);

    if (currentIndex < sentenceList.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentChunkIndex(0);
      return;
    }

    const modeIndex = availableModeList.findIndex(
      (mode) => mode.key === modeKey
    );
    const nextMode = availableModeList[modeIndex + 1];

    // 통역은 단계를 다 끝낸 뒤 AI 가 한 번에 채점한다. 그래서 '다시 시킬지' 는
    // 여기서만 알 수 있다. 기준에 못 미친 문장이 있으면 그 자리로 돌려보낸다.
    const failedIndexList = await gradeInterpretationLog();
    if (modeKey === "interpretation" && failedIndexList.length > 0) {
      setInterpretationRetryNotice(
        `${failedIndexList.length}문장이 목표 점수에 못 미쳐 다시 합니다.`
      );
      setCurrentIndex(failedIndexList[0]);
      setCurrentChunkIndex(0);
      // 다시 하는 자리는 진도에서 뺀다. 그래야 그 문장을 또 낼 수 있다.
      failedIndexList.forEach((index) =>
        doneKeySetRef.current.delete(`interpretation:${index}`)
      );
      return;
    }

    setInterpretationRetryNotice(null);

    if (nextMode) {
      setModeKey(nextMode.key);
      setCurrentIndex(0);
      setCurrentChunkIndex(0);
      return;
    }

    finishSection();
  };

  const currentEnglish = sentenceList[currentIndex]?.[0] || "";

  // 주어진 글에 나오는 단어만 골라 낸다. 끊어읽기 단위면 그 덩어리에, 문장
  // 단위면 그 문장에 나오는 것이다.
  const wordListOfText = (english) => {
    const lowered = (english || "").toLowerCase();
    return wordList.filter(
      (word) => word.eng && lowered.includes(word.eng.toLowerCase())
    );
  };

  // 지금 읽는 만큼에 나오는 단어를 목록 맨 위로 올린다. 스무 개를 훑어 내려가며
  // 어느 것이 지금 필요한 단어인지 찾게 두면 공부가 안 된다. 번호는 원래 것을
  // 그대로 들고 가서 아래 전체 목록과 같은 단어임을 알 수 있게 한다.
  const currentUnitEnglish =
    highlightScope === "chunk"
      ? chunkPairListOf(currentIndex)[currentChunkIndex]?.[0] || currentEnglish
      : currentEnglish;


  const orderedWordList = wordList
    .map((word, index) => ({
      word,
      index,
      isCurrent: currentUnitEnglish
        .toLowerCase()
        .includes((word.eng || "").toLowerCase()),
    }))
    .sort((left, right) => Number(right.isCurrent) - Number(left.isCurrent));

  const renderSentence = (sentence, index) => {
    const [english, korean] = sentence;
    const isCurrent = index === currentIndex;
    const chunkPairList = buildChunkPairList(english, korean);

    // 모드마다 위(크게)와 아래(작게)에 무엇을 둘지만 다르다.
    // 듣기 모드에서 뒤집으면 우리말이 위로 온다. 우리말을 보고 영어를 떠올리는
    // 연습이라, 영어를 아래에 작게 두어 곧바로 확인할 수 있게 한다.
    const isKoreanFirst =
      modeKey === "listening" && listeningLayout === "korean";

    const takeLead = (englishChunk, koreanChunk) =>
      isKoreanFirst ? koreanChunk : englishChunk;

    /**
     * 그 덩어리의 글자를 **구문분석을 그려 넣은 것으로 갈아 끼운다.**
     *
     * 아이가 읽는 큰 글자 자체에 그린다. 작은 글자로 문장을 한 번 더 그려 위에
     * 얹어 봤더니 같은 글이 두 번 나와 어느 것을 읽어야 할지 흐려졌다.
     *
     * **지금 읽는 덩어리만이 아니라 문장의 모든 덩어리에 그린다.** 하나만 그리면
     * 조각만 남고 앞에서 열린 절의 닫는 괄호가 덩그러니 떨어진다. 나란히 다 그리면
     * 밑줄이 옆으로 이어져 문장 구조가 통째로 드러나고, 지금 읽는 덩어리는 원래대로
     * 진하게 보인다.
     *
     * 뒤집기(`korean_first`)에서는 영어를 감추는 중이라 그리지 않는다. 그리면
     * 떠올려야 할 영어가 그대로 보여서 그 단계를 둔 뜻이 사라진다.
     */
    const syntaxOf = (chunkIndex, fallback) => {
      if (!isSyntaxOn || !isCurrent || isKoreanFirst) return null;
      const item = syntaxItemList[index];
      if (!item) return null;
      return (
        <SyntaxHint
          item={item}
          englishSentence={english}
          chunkIndex={chunkIndex}
          fallback={fallback}
        />
      );
    };

    // 통역에서는 우리말 자리를 **비워 두었다가 아이가 말한 것으로 채운다.**
    // 모범 해석을 그대로 두면 스스로 옮길 일이 없고, 다 하고 나서 무엇을 어떻게
    // 옮겼는지도 남지 않는다. 채점 결과 창은 지나가지만 이 자리는 남는다.
    const spokenKoreanOf = (chunkIndex) =>
      spokenKoreanMap[[modeIndexOf(modeKey), index, chunkIndex].join(":")];

    const interpretationScoreOf = (chunkIndex) =>
      interpretationScoreMap[
        [modeIndexOf(modeKey), index, chunkIndex].join(":")
      ];

    const takeSub = (englishChunk, koreanChunk, chunkIndex) => {
      if (modeKey === "interpretation") return spokenKoreanOf(chunkIndex) || null;
      // 해석 숨기기: 우리말을 안 보여준다. 자리는 남겨 둔다(아래 참고).
      if (modeKey === "listening" && listeningLayout === "hidden") return null;
      // 뒤집기: 우리말을 보고 영어를 떠올리는 연습이라, **지금 읽고 있는 덩어리의
      // 영어는 감춘다.** 보이면 떠올릴 것이 없다. 지나간 덩어리는 확인용으로 남긴다.
      if (isKoreanFirst) {
        const isCurrentChunk = isCurrent && chunkIndex === currentChunkIndex;
        return isCurrentChunk ? null : englishChunk;
      }
      return koreanChunk;
    };

    return (
      <div
        key={index}
        ref={isCurrent ? currentSentenceRef : null}
        className={`direct-translation__sentence${
          isCurrent ? " is-current" : ""
        }${isCurrent && isStudentTurn ? " is-waiting" : ""}`}
        onClick={() => handleSentenceClick(index)}
      >
        <span className="direct-translation__sentence-number">
          {numberOf(index)}
        </span>

        {chunkPairList ? (
          <span className="direct-translation__chunks">
            {chunkPairList.map(([englishChunk, koreanChunk], chunkIndex) => {
              const sub = takeSub(englishChunk, koreanChunk, chunkIndex);
              const isLastChunk = chunkIndex === chunkPairList.length - 1;
              // 지금 읽는 덩어리 하나만 켠다.
              const isCurrentChunk =
                highlightScope === "chunk" &&
                isCurrent &&
                chunkIndex === currentChunkIndex;

              return (
                <span
                  key={chunkIndex}
                  className={`direct-translation__chunk${
                    isCurrentChunk ? " is-current" : ""
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChunkClick(index, chunkIndex);
                  }}
                >
                  {/* 슬래시는 덩어리 사이가 아니라 **덩어리 끝에 붙여** 그린다.
                      사이에 두면 줄이 바뀔 때 슬래시만 다음 줄 맨 앞으로 넘어가
                      "/ is that /" 처럼 어디서 끊는지가 되레 흐려진다. */}
                  <span className="direct-translation__chunk-line">
                    <span className="direct-translation__chunk-lead">
                      {/* **지금 진하게 표시된 덩어리에만 그린다.** 문장 전체에
                          그렸더니 이름표가 화면을 뒤덮어 정작 읽을 글이 묻혔다.
                          아이가 보는 자리 하나만 또렷하면 된다. */}
                      {(isCurrentChunk &&
                        syntaxOf(
                          chunkIndex,
                          takeLead(englishChunk, koreanChunk)
                        )) ||
                        takeLead(englishChunk, koreanChunk)}
                    </span>
                    {!isLastChunk && (
                      <span className="direct-translation__chunk-divider">
                        /
                      </span>
                    )}
                  </span>
                  {/* 아래 줄이 비어도 자리는 남긴다. 없애 버리면 덩어리마다 높이가
                      달라져 글이 위아래로 널뛴다(해석 숨기기에서 그랬다). */}
                  <span
                    className={`direct-translation__chunk-sub${
                      sub || interpretationScoreOf(chunkIndex)
                        ? ""
                        : " is-empty"
                    }`}
                  >
                    {sub}
                    {/* 채점 결과. 그 덩어리 바로 밑에 붙어야 무엇이 몇 점인지
                        보인다. 결과 창은 지나가지만 이 자리는 남는다. */}
                    {interpretationScoreOf(chunkIndex) && (
                      <span
                        className={`direct-translation__piece-score${
                          interpretationScoreOf(chunkIndex).isCorrect
                            ? " is-correct"
                            : " is-wrong"
                        }`}
                      >
                        {interpretationScoreOf(chunkIndex).score}점
                      </span>
                    )}
                  </span>
                </span>
              );
            })}
          </span>
        ) : (
          <span className="direct-translation__chunks direct-translation__plain">
            <span className="direct-translation__plain-lead">
              {syntaxOf(null, takeLead(english, korean)) ||
                takeLead(english, korean)}
            </span>
            {takeSub(english, korean, 0) && (
              <span className="direct-translation__plain-sub">
                {takeSub(english, korean, 0)}
              </span>
            )}
          </span>
        )}

      </div>
    );
  };

  // 삽화가 없으면 그 칸을 단어에 넘긴다. 빈 상자를 세워 두면 좁은 화면에서
  // 정작 읽어야 할 본문이 밀린다.
  const hasIllustration = Boolean(illustrationUrl);

  // 모바일은 왼쪽 칸을 세로로 세울 자리가 없어 삽화와 단어를 탭으로 바꿔 가며 본다.
  const sideTabs = isMobile && hasIllustration ? (
    <span className="direct-translation__side-tabs">
      <button
        className={`direct-translation__mode${
          sideTab === "words" ? " is-on" : ""
        }`}
        onClick={() => setSideTab("words")}
      >
        단어
      </button>
      <button
        className={`direct-translation__mode${
          sideTab === "illustration" ? " is-on" : ""
        }`}
        onClick={() => setSideTab("illustration")}
      >
        삽화
      </button>
    </span>
  ) : null;

  const illustrationPane = (
    <div className="direct-translation__pane direct-translation__illustration">
      <div className="direct-translation__pane-head">
        삽화
        {sideTabs}
      </div>
      <img
        className="direct-translation__illustration-image"
        src={illustrationUrl}
        alt="지문 삽화"
      />
    </div>
  );

  const wordPane = (
    <div className="direct-translation__pane direct-translation__words">
      <div className="direct-translation__pane-head">
        단어
        {sideTabs}
      </div>
      <div className="direct-translation__pane-scroll">
        {wordList.length === 0 ? (
          <div className="direct-translation__illustration-empty">
            이 지문에는 단어 정보가 없습니다.
          </div>
        ) : (
          orderedWordList.map(({ word, index, isCurrent }, position) => {
            // 지금 단어가 끝나고 나머지가 시작되는 자리에 선을 긋는다.
            const isFirstRest =
              !isCurrent && position > 0 && orderedWordList[position - 1].isCurrent;

            return (
              <div
                key={index}
                className={`direct-translation__word${
                  isCurrent ? " is-current" : ""
                }${isFirstRest ? " is-rest-start" : ""}`}
              >
                <span className="direct-translation__word-number">
                  {index + 1}
                </span>
                <span className="direct-translation__word-english">
                  {word.eng}
                </span>
                <span className="direct-translation__word-korean">
                  {word.kor}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const currentMode =
    availableModeList.find((mode) => mode.key === modeKey) || MODE_LIST[0];
  const isStudentTurn = STUDENT_TURN_LIST.includes(turn);
  // 진도의 단위는 '문장 × 고른 단계' 다. 서버의 계산과 같아야 한다
  // (api/assignment/views.py 의 direct_translation 분기).
  const totalStepCount = sentenceList.length * availableModeList.length;

  return (
    <div
      className={`direct-translation${
        isMobile ? " direct-translation--mobile" : ""
      }${highlightScope === "chunk" ? " direct-translation--by-chunk" : ""}${
        isSyntaxOn ? " direct-translation--syntax-on" : ""
      }`}
      style={{
        height: getContentHeight(isMobile),
        "--direct-translation-text-scale": textScale,
      }}
    >
      <StepsBar />

      <div className="direct-translation__body">
        {!isMobile && (
          <div className="direct-translation__side">
            {hasIllustration && illustrationPane}
            {wordPane}
          </div>
        )}

        <div className="direct-translation__pane direct-translation__main">
          <div className="direct-translation__pane-head">
            <span className="direct-translation__pane-title">직독직해</span>
            <span className="direct-translation__text-scale">
              {TEXT_SCALE_LIST.map((scale) => (
                <button
                  key={scale}
                  className={`direct-translation__scale-button${
                    textScale === scale ? " is-on" : ""
                  }`}
                  onClick={() => handleTextScaleClick(scale)}
                  title={TEXT_SCALE_LABEL[scale]}
                  style={{ fontSize: `${11 * scale}px` }}
                >
                  가
                </button>
              ))}
            </span>
            {syntaxItemList.length > 0 && (
              <button
                className={`direct-translation__syntax-toggle${
                  isSyntaxOn ? " is-on" : ""
                }`}
                onClick={() => setIsSyntaxOn((previous) => !previous)}
                title="지금 읽는 자리의 문장 구조를 함께 봅니다"
              >
                구문
              </button>
            )}
            <span className="direct-translation__modes">
              {availableModeList.map((mode, index) => (
                <button
                  key={mode.key}
                  className={`direct-translation__mode${
                    mode.key === modeKey ? " is-on" : ""
                  }`}
                  onClick={() => handleModeClick(mode.key)}
                >
                  {index + 1} {mode.value}
                </button>
              ))}
            </span>
          </div>

          {isMicrophoneUnavailable && (
            <div className="direct-translation__call direct-translation__call--warning">
              이 브라우저는 발음 확인을 지원하지 않아 점수 없이 진행합니다.
              크롬이나 사파리로 열어 보세요.
            </div>
          )}

          {/* 듣기 모드에서 아이가 말할 차례. 마이크가 없어 화면에 아무 표시가
              없으면 그냥 멈춘 줄 안다. */}
          {!isListening && turn === "repeat" && !isMicrophoneTurn && (
            <div className="direct-translation__call">
              {listeningLayout === "korean"
                ? "영어로 말해 보세요"
                : "따라 말해 보세요"}
            </div>
          )}

          {/* 듣는 중일 때만 띄운다. 무엇을 누르라는 안내는 아래 버튼이 이미
              말하고 있어서, 위에 또 적으면 읽을 글을 밀어낼 뿐이다. */}
          {isListening && (
            <div
              className="direct-translation__call direct-translation__call--recording"
              onClick={finishListening}
            >
              <MicrophoneMark />
              <WaveformMark isActive={isSpeechDetected} />
              듣고 있어요
            </div>
          )}

          <div className="direct-translation__stage">
            <div className="direct-translation__pane-scroll">
              {sentenceList.map(renderSentence)}
            </div>

          {/* 기준에 못 미쳐 앞으로 돌아왔을 때. 아무 말 없이 돌려보내면 아이는
              화면이 튄 줄 안다. 다음 문장으로 넘어가면 저절로 사라진다. */}
          {interpretationRetryNotice && (
            <div className="direct-translation__hint is-retry">
              {interpretationRetryNotice}
            </div>
          )}

          {/* 통역 채점은 AI 가 하느라 몇 초 걸린다. 도는 표시를 보여주지 않으면
              아이는 아무 일도 안 일어난 줄 알고 버튼을 또 누른다. */}
          {isGradingInterpretation && (
            <div className="direct-translation__score is-grading">
              <span className="direct-translation__spinner" />
              <div className="direct-translation__score-message">채점 중</div>
              <div className="direct-translation__score-detail">
                통역한 말을 살펴보고 있어요
              </div>
            </div>
          )}

          {/* 통역 단계 총평. 한 번은 보고 넘어가야 무엇을 받았는지 안다. */}
          {!isGradingInterpretation && interpretationSummary && (
            <div
              className={`direct-translation__score is-${
                gradeOf(interpretationSummary.score, interpretationPassingScore).key
              }`}
              onClick={() => setInterpretationSummary(null)}
            >
              <GradeMark gradeKey={gradeOf(interpretationSummary.score, interpretationPassingScore).key} />
              <div className="direct-translation__score-message">
                {gradeOf(interpretationSummary.score, interpretationPassingScore).message}
              </div>
              <div className="direct-translation__score-accuracy">
                통역 {interpretationSummary.score}점
              </div>
              <div className="direct-translation__score-detail">
                {interpretationSummary.count}개 문장을 통역했어요 · 눌러서 닫기
              </div>
            </div>
          )}

          {/* 채점 결과는 본문 위에 큼직하게 얹는다. 아래 조작 줄의 작은 글씨로는
              읽으면서 눈을 떼지 않는 아이가 알아채지 못한다. */}
          {readingResult && (
            <div
              className={`direct-translation__score is-${
                gradeOf(readingResult.accuracy, fluencyPassingScore).key
              }`}
              onClick={handleSkipReadingClick}
            >
              {readingResult.isScoringUnavailable ? (
                // 채점을 못 한 경우. 목소리를 담지 않으므로 들려줄 것이 없다.
                // 예전 문구("내 목소리로 확인해요")는 녹음이 있을 때의 말이었다.
                <div className="direct-translation__score-message">
                  잘 안 들렸어요. 다음으로 넘어갈게요
                </div>
              ) : (
                <>
                  <GradeMark gradeKey={gradeOf(readingResult.accuracy, fluencyPassingScore).key} />
                  <div className="direct-translation__score-accuracy">
                    {readingResult.accuracy}%
                  </div>
                  <div className="direct-translation__score-message">
                    {gradeOf(readingResult.accuracy, fluencyPassingScore).message}
                  </div>
                </>
              )}
              {/* 받아쓴 문장은 보여주지 않는다. 헛들은 것이 그대로 뜨면 아이가
                  제 발음을 의심하게 되고, 읽는 데 도움도 안 된다. */}
              {!readingResult.isInterpretation &&
                !readingResult.isScoringUnavailable && (
                  <div className="direct-translation__score-detail">
                    {gradeOf(readingResult.accuracy, fluencyPassingScore).detail}
                  </div>
                )}

            </div>
          )}
          </div>

          {/* 조작 줄은 두 층이다. 위는 설정(한 번 정하면 잘 안 건드린다),
              아래는 지금 눌러야 할 것. 한 줄에 몰아 넣었더니 글자가 낱자로
              쪼개져 나왔다. */}
          <div className="direct-translation__foot">
            {/* 설정 버튼을 두지 않는다. 읽는 단위·단계·채점은 모두 교사가 출제할
                때 정한다. 아이 화면에는 아이가 할 일만 둔다. */}

            {/* 듣기 모드에서만 나오는 두 가지. 다른 단계에서는 뜻이 없다. */}
            {modeKey === "listening" && (
              <div className="direct-translation__foot-settings">
                <button
                  className={`direct-translation__mode${
                    isListeningManual ? " is-on" : ""
                  }`}
                  onClick={() => {
                    stopPlaying();
                    setIsListeningManual((previous) => !previous);
                  }}
                >
                  {isListeningManual ? "하나씩 넘기기" : "자동으로 넘기기"}
                </button>
                {LISTENING_LAYOUT_LIST.map((layout) => (
                  <button
                    key={layout.key}
                    className={`direct-translation__mode${
                      listeningLayout === layout.key ? " is-on" : ""
                    }`}
                    onClick={() => setListeningLayout(layout.key)}
                  >
                    {layout.value}
                  </button>
                ))}
              </div>
            )}

            <div className="direct-translation__foot-actions">
              <span className="direct-translation__count">
                {sentenceList.length > 0 ? currentIndex + 1 : 0} /{" "}
                {sentenceList.length}
                {isMobile ? "" : " 문장"}
                {playUnit === "chunk" && (
                  <>
                    {" · "}
                    {currentChunkIndex + 1} /{" "}
                    {chunkPairListOf(currentIndex).length}
                  </>
                )}
                {totalStepCount > 0 && (
                  <>
                    {" · 진도 "}
                    {Math.floor((doneCount / totalStepCount) * 100)}%
                  </>
                )}
              </span>
              {/* 단계별 점수. 유창성은 읽을 때마다 갱신되고, 통역은 채점이 끝나면
                  들어와 그대로 남는다. 하나로 뭉쳐 놓으면 무엇을 몇 점 받았는지
                  알 수가 없다. */}
              {availableModeList
                .filter((mode) => modeScoreMap[mode.key] !== undefined)
                .map((mode) => (
                  <span
                    key={mode.key}
                    className="direct-translation__score-badge"
                  >
                    {mode.value} {modeScoreMap[mode.key]}점
                  </span>
                ))}
              {isPlayingMyVoice && (
                <span className="direct-translation__turn is-listen">
                  내가 읽은 것을 들려드릴게요
                </span>
              )}
              {turn && !readingResult && !isPlayingMyVoice && (
                <span className={`direct-translation__turn is-${turn}`}>
                  {isListening
                    ? "듣는 중..."
                    : turn === "repeat" &&
                        modeKey === "listening" &&
                        listeningLayout === "korean"
                      ? "영어로 말해요"
                      : TURN_LABEL[turn]}
                  {isStudentTurn && !isListening && !isMobile && " (스페이스바)"}
                </span>
              )}
              {readingResult && readingResult.accuracy < fluencyPassingScore && (
                <button
                  className="direct-translation__button direct-translation__button--ghost"
                  onClick={handleSkipReadingClick}
                >
                  건너뛰기
                </button>
              )}
              {/* 아이 차례에는 감춘다. 지금 누를 것이 하나여야 헷갈리지 않는다. */}
              {!isStudentTurn && (
                <button
                  className="direct-translation__button direct-translation__button--ghost"
                  onClick={handlePlayClick}
                >
                  {isPlaying ? "■ 멈춤" : "▶ 듣기"}
                </button>
              )}
              {/* 녹음·채점이 켜진 과제에서는 아무 때나 눌러 지금 문장을 읽어볼 수
                  있어야 한다. 재생을 시작해야만 열리면 있는 줄도 모른다. */}
              {!isStudentTurn &&
                !isPlaying &&
                (isMicrophoneTurn || isRecordingAllowed) &&
                isSpeechRecognitionSupported && (
                  <button
                    className={`direct-translation__button direct-translation__button--record${
                      isListening ? " is-listening" : ""
                    }`}
                    onClick={handleRecordNowClick}
                  >
                    <MicrophoneMark /> 읽기
                  </button>
                )}
              {/* 듣는 중에는 감춘다. 말을 멈추면 인식이 알아서 끊긴다. 잘못 눌러
                  중간에 끊기는 것이 버튼이 있어서 얻는 것보다 크다. 오래 안 끊기면
                  위 띠를 눌러 끝낼 수 있다. */}
              {!isListening && (
                <button
                  className={`direct-translation__button${
                    isStudentTurn && isMicrophoneTurn && !readingResult
                      ? " direct-translation__button--record"
                      : ""
                  }`}
                  onClick={handleNextClick}
                >
                  {turn === "listen" ? (
                    "듣는 중..."
                  ) : !isStudentTurn ? (
                    "다음 →"
                  ) : !isMicrophoneTurn ? (
                    // 듣기 수동은 마이크가 없다. 따라 말해 보고 넘기기만 한다.
                    "다음 →"
                  ) : (
                    <>
                      <MicrophoneMark /> 읽기
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {isMobile && (
          <div className="direct-translation__side">
            {hasIllustration && sideTab === "illustration"
              ? illustrationPane
              : wordPane}
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectTranslationPage;
